import React from "react";
import { Drawer } from "@mui/material";
import { usePosts, useStream } from "./hooks";
import { DrawerHeader } from "./DrawerHeader";
import { DataBody } from "./DataBody";
import { InfoSidebar } from "./InfoSidebar";

// status 0 → raw  + stream
// status 1 → raw  + API
// status 2 → raw  + API
// status 3 → processed + stream
// status 4 → tab-driven + API
function resolveDataParams(status, fallbackTab) {
  switch (status) {
    case 0: return { dataType: "raw",       isStreaming: true  };
    case 1: return { dataType: "raw",       isStreaming: false };
    case 2: return { dataType: "raw",       isStreaming: false };
    case 3:
      if (fallbackTab === "raw") return { dataType: "raw",       isStreaming: false };
      return                            { dataType: "processed", isStreaming: true  };
    case 4: return { dataType: fallbackTab, isStreaming: false };
    default: return { dataType: fallbackTab, isStreaming: false };
  }
}

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Host+Grotesk:wght@400;500;600;700;800&display=swap');

  @keyframes slideIn { from { opacity:0; transform:translateY(6px) }  to { opacity:1; transform:translateY(0) } }
  @keyframes pulse   { 0%,100% { opacity:1 } 50% { opacity:0.35 } }
  @keyframes spin    { to { transform:rotate(360deg) } }
  @keyframes glow    { 0%,100% { box-shadow:0 0 0 3px rgba(16,185,129,0.15) } 50% { box-shadow:0 0 0 6px rgba(16,185,129,0.04) } }

  .sdd-scroll::-webkit-scrollbar       { width:3px }
  .sdd-scroll::-webkit-scrollbar-track { background:transparent }
  .sdd-scroll::-webkit-scrollbar-thumb { background:#E5E7EB; border-radius:3px }
`;

export default function SessionDataDrawer({
  open,
  onClose,
  session,
  initialTab = "raw",
}) {
  if (!session) return null;

  const sessionId = session.sessionId || session._id;
  const { dataType, isStreaming } = resolveDataParams(session.status, initialTab);

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "min(1040px, 96vw)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            fontFamily: "'Host Grotesk', sans-serif",
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <DrawerInner
          session={session}
          sessionId={sessionId}
          isStreaming={isStreaming}
          dataType={dataType}
          onClose={onClose}
        />
      </Drawer>
    </>
  );
}

function DrawerInner({ session, sessionId, isStreaming, dataType, onClose }) {
  const sessionType = session.type === "sentiment" ? "sentiment" : "section";
  const rest = usePosts({ sessionId, dataType, sessionType, enabled: true });
  const stream = useStream({ sessionId, dataType, sessionType, enabled: isStreaming });

  const data = isStreaming
    ? {
        // New live posts prepended; REST posts deduplicated against stream
        posts: [
          ...stream.posts,
          ...rest.posts.filter((p) => !stream.streamedIds.has(p._id)),
        ],
        loading: rest.loading,
        loadingMore: rest.loadingMore,
        error: rest.error || stream.error,
        onRetry: rest.retry,
        totalCount: rest.totalCount + stream.newLiveCount,
        hasMore: rest.hasMore,
        onLoadMore: rest.loadMore,
      }
    : {
        posts: rest.posts, loading: rest.loading, loadingMore: rest.loadingMore,
        error: rest.error, onRetry: rest.retry,
        totalCount: rest.totalCount, hasMore: rest.hasMore, onLoadMore: rest.loadMore,
      };

  return (
    <>
      <DrawerHeader
        session={session}
        tab={dataType}
        useStream={isStreaming}
        streamConnected={stream.connected}
        newLiveCount={stream.newLiveCount}
        totalCount={data.totalCount}
        onClose={onClose}
      />

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <DataBody
            tab={dataType}
            session={session}
            sessionType={sessionType}
            posts={data.posts}
            loading={data.loading}
            loadingMore={data.loadingMore}
            error={data.error}
            onRetry={data.onRetry}
            useStream={isStreaming}
            streamConnected={stream.connected}
            newLiveCount={stream.newLiveCount}
            streamedIds={stream.streamedIds}
            totalCount={data.totalCount}
            hasMore={data.hasMore}
            onLoadMore={data.onLoadMore}
          />
        </div>

        <InfoSidebar
          session={session}
          sessionId={sessionId}
          useStream={isStreaming}
          streamConnected={stream.connected}
        />
      </div>
    </>
  );
}
